import { INTERACT, JUMP_VELOCITY } from "./config.ts";
import { clamp, type Pose, type Vec3 } from "./math.ts";
import { WORLD_LADDERS, ladderPoint, worldLadderEnd, type WorldLadder } from "./interactives.ts";
import { FRAME_WORLD, MODE_WALK, nearLadder, placeInWorld, type Body, type MoveInput } from "./physics.ts";
import { SHIP_POINTS, deckLadderEnd, ladderPosition, type DeckLadderEnd } from "./ship.ts";

/**
 * Counter-Strike style ladders shared by the server's authority and offline tools (the island lab),
 * so a climb takes the same contact, path, pace and landing everywhere. Ladder frames point +x out
 * of the rungs toward the climber; `heading` is that frame's world heading (tower frame or hull).
 */

export interface LadderDrive {
  /** Climb pace, −1 (down) … 1 (up). */
  rate: number;
  /** Horizontal push into the rungs, −1 (away) … 1 (into), ignoring look pitch. */
  push: number;
}

/**
 * Source-engine ladder movement: input moves along the full view direction, and its vertical part
 * plus its push into the rungs becomes climbing. Facing the rungs, W climbs and looking well down
 * descends; facing away (walking off a deck onto the ladder) W descends; S does the reverse.
 */
export function ladderDrive(input: Pick<MoveInput, "mx" | "mz" | "pitch">, yaw: number, heading: number, out: LadderDrive): LadderDrive {
  let mx = clamp(input.mx, -1, 1), mz = clamp(input.mz, -1, 1);
  const len = Math.hypot(mx, mz);
  if (len > 1) { mx /= len; mz /= len; }
  const sy = Math.sin(yaw), cy = Math.cos(yaw), ch = Math.cos(heading), sh = Math.sin(heading);
  // World move (x, z) as stepBody steers it; the frame's outward axis is (cos h, −sin h).
  const moveX = cy * mx + sy * mz, moveZ = -sy * mx + cy * mz;
  const fwdInto = -(sy * ch - cy * sh), rightInto = -(cy * ch + sy * sh);
  out.push = -(moveX * ch - moveZ * sh);
  out.rate = clamp(-mz * Math.sin(input.pitch) + mz * Math.cos(input.pitch) * fwdInto + mx * rightInto, -1, 1);
  return out;
}

/** Moves `progress` (0 foot, 1 top) by `rate` full ladders per `seconds`, snapped to 0 or 1 at the ends. */
export function advanceClimb(progress: number, rate: number, dt: number, seconds: number): number {
  const next = progress + rate * dt / seconds;
  return next <= 1e-6 ? 0 : next >= 1 - 1e-6 ? 1 : next;
}

/** The end a climb at `progress` steps off by pushing past it at `rate`: 0 the foot, 1 the top, or -1 (stay on). */
export function climbArrival(progress: number, rate: number): number {
  return progress === 0 && rate < 0 ? 0 : progress === 1 && rate > 0 ? 1 : -1;
}

/** Walking into the foot while climbing up, or out over the head while climbing down, mounts the ladder. */
function mounts(drive: LadderDrive, top: boolean): boolean {
  return top ? drive.push < -INTERACT.ladderMountPush && drive.rate < 0 : drive.push > INTERACT.ladderMountPush && drive.rate > 0;
}

export interface TowerGrab {
  /** WORLD_LADDERS index. */
  index: number;
  ladder: WorldLadder;
  /** Mounted at the deck end: the climb starts at the top. */
  down: boolean;
}

const point: Vec3 = { x: 0, y: 0, z: 0 };
const drive: LadderDrive = { rate: 0, push: 0 };

/** The tower ladder a world-frame body walks onto with this input, or null. */
export function towerLadderContact(b: Body, input: Pick<MoveInput, "mx" | "mz" | "pitch">): TowerGrab | null {
  if (b.frame !== FRAME_WORLD) return null;
  for (let index = 0; index < WORLD_LADDERS.length; index++) {
    const ladder = WORLD_LADDERS[index]!;
    const end = worldLadderEnd(ladder, b.x, b.y, b.z, INTERACT.ladderRange);
    if (end && mounts(ladderDrive(input, b.yaw, ladder.frame.heading, drive), end === "top")) return { index, ladder, down: end === "top" };
  }
  return null;
}

/** True when a swimmer pushes into a hull's ladder foot with this input. */
export function hullLadderContact(b: Body, ship: Pose, input: Pick<MoveInput, "mx" | "mz" | "pitch">): boolean {
  return nearLadder(b, ship, SHIP_POINTS.ladderBottom, INTERACT.ladderRange) && mounts(ladderDrive(input, b.yaw, ship.heading, drive), false);
}

/** The ship ladder end a sailor walking aboard (ship-local body) mounts with this input, or null. */
export function deckLadderContact(b: Body, input: Pick<MoveInput, "mx" | "mz" | "pitch">): DeckLadderEnd | null {
  if (b.frame === FRAME_WORLD || b.mode !== MODE_WALK) return null;
  const end = deckLadderEnd(b.x, b.y, b.z);
  // Ship-local yaw against the ship-local ladder frame.
  return end && mounts(ladderDrive(input, b.yaw, 0, drive), end.end === "top") ? end : null;
}

/** Jumps off the rungs: a small hop out from the ladder face, then an ordinary fall. */
export function jumpOffLadder(b: Body, heading: number): void {
  b.x += Math.cos(heading) * INTERACT.ladderJumpOff;
  b.z -= Math.sin(heading) * INTERACT.ladderJumpOff;
  releaseLadder(b);
  b.vy = JUMP_VELOCITY * 0.5;
}

/** Hangs the body on the rungs at `progress` (0 foot, 1 deck), keeping its yaw. */
export function mountTowerLadder(ladder: WorldLadder, b: Body, progress: number): void {
  ladderPoint(ladder, ladderPosition(progress, point, ladder.spec), point);
  placeInWorld(b, point.x, point.y, point.z, b.yaw);
  b.grounded = false;
}

/** Advances a climb at `rate` (-1 … 1) for `dt` and carries the body; returns the new progress. */
export function stepTowerClimb(ladder: WorldLadder, b: Body, progress: number, rate: number, dt: number): number {
  const next = advanceClimb(progress, rate, dt, ladder.spec.seconds);
  ladderPoint(ladder, ladderPosition(next, point, ladder.spec), point);
  b.x = point.x; b.y = point.y; b.z = point.z;
  b.vy = 0; b.fallStartY = b.y;
  return next;
}

/** Lets go of any ladder: the body falls from here. */
export function releaseLadder(b: Body): void {
  b.fallStartY = b.y;
  b.vy = 0;
  b.grounded = false;
}

/** Steps off at the end reached: the foot after climbing down, the deck after climbing up. */
export function leaveTowerLadder(ladder: WorldLadder, b: Body, down: boolean): void {
  ladderPoint(ladder, down ? ladder.spec.bottom : ladder.spec.top, point);
  placeInWorld(b, point.x, point.y, point.z, b.yaw);
}
